import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function Login() {
  const { signInEmail } = useAuth();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyGoogle, setBusyGoogle] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'signup') {
        // Signup via backend (bypass email confirmation)
        const backend = process.env.REACT_APP_BACKEND_URL;
        const resp = await fetch(`${backend}/api/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Signup fallito');
        }
        const { error } = await signInEmail(email, password);
        if (error) throw error;
        toast.success('Account creato. Sei dentro.');
      } else {
        const { error } = await signInEmail(email, password);
        if (error) throw error;
      }
    } catch (err) {
      toast.error(err.message || 'Errore');
    } finally {
      setBusy(false);
    }
  };

  // Google OAuth nativo via Supabase Auth (provider Google).
  // Supabase fa redirect a Google, gestisce la callback su
  // `${SUPABASE_URL}/auth/v1/callback` e poi ti riporta su `redirectTo`.
  // La sessione finisce nel localStorage `sb-*-auth-token`; il listener
  // `onAuthStateChange` (in lib/auth.jsx) la raccoglie automaticamente.
  const signInGoogle = async () => {
    if (busyGoogle) return;
    setBusyGoogle(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/',
          // queryParams: { access_type: 'offline', prompt: 'consent' }, // se vuoi refresh token
        },
      });
      if (error) throw error;
      // Se non c'è errore, il browser sta già navigando verso Google;
      // setBusyGoogle resta true fino al redirect.
    } catch (err) {
      toast.error(err.message || 'Errore login Google');
      setBusyGoogle(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-10 flex flex-col items-center text-center">
          <svg
            width="72"
            height="72"
            viewBox="0 0 46 46"
            role="img"
            aria-label="Quantum Planner"
            className="mb-4"
            data-testid="login-logo-svg"
          >
            {/* Barre nei 4 colori-area del design system (constants/areas.js) */}
            <rect x="6" y="9" width="34" height="5.5" rx="2.75" fill="#E5484D" />
            <rect x="6" y="18.5" width="26" height="5.5" rx="2.75" fill="#2563EB" />
            <rect x="6" y="28" width="18" height="5.5" rx="2.75" fill="#F97316" />
            <rect x="6" y="37.5" width="10" height="5.5" rx="2.75" fill="#475569" />
          </svg>
          <div
            className="font-display tracking-tight text-2xl leading-none mb-6"
            data-testid="login-brand"
          >
            <span style={{ fontWeight: 700, color: 'var(--qp-ink)' }}>Quantum</span>
            <span style={{ fontWeight: 400, color: 'var(--qp-text-soft)' }}> Planner</span>
          </div>
          <h1 className="font-display text-4xl tracking-tight leading-tight">
            {mode === 'signin' ? 'Le settimane buone si scrivono prima.' : 'Iniziamo dal piano.'}
          </h1>
          <p className="font-body text-sm text-soft mt-2">
            {mode === 'signin' ? 'Entra e riprendi da dove eri.' : 'Bastano email e password.'}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          <div>
            <label className="mono-label block mb-1">Email</label>
            <Input
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-line focus-visible:ring-0 focus-visible:border-ink h-11 font-body"
              data-testid="input-email"
            />
          </div>
          <div>
            <label className="mono-label block mb-1">Password</label>
            <Input
              type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none border-line focus-visible:ring-0 focus-visible:border-ink h-11 font-body"
              data-testid="input-password"
            />
          </div>
          <Button
            type="submit" disabled={busy}
            className="w-full rounded-none bg-ink hover:bg-accent text-canvas font-mono text-xs uppercase tracking-[0.2em] h-11"
            data-testid="submit-btn"
          >
            {busy && <Loader2 size={14} className="animate-spin mr-2" />}
            {mode === 'signin' ? 'Entra' : 'Crea account'}
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-between text-xs font-mono">
          <button
            onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
            className="uppercase tracking-[0.18em] text-soft hover:text-ink"
            data-testid="toggle-mode-btn"
          >
            {mode === 'signin' ? 'Non hai account? Crealo' : 'Hai già un account? Entra'}
          </button>
        </div>

        <div className="mt-10 border-t border-line pt-6">
          <button
            type="button"
            onClick={signInGoogle}
            disabled={busyGoogle}
            className="w-full font-mono text-xs uppercase tracking-[0.2em] text-ink border border-ink hover:bg-ink hover:text-canvas transition-colors h-11 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="google-btn"
          >
            {busyGoogle && <Loader2 size={14} className="animate-spin" />}
            <GoogleIcon />
            Accedi con Google
          </button>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-mute text-center">
            Nxlex Srls — All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
}

// Logo Google a 4 colori (path ufficiale Google Identity)
function GoogleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 5.9 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.9 29.5 5 24 5 16.3 5 9.7 9.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.4 0 10.3-1.9 14-5l-6.5-5.3C29.4 35 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.5 5.3C40.7 35.7 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
